/**
 * CapabilitiesCell
 *
 * The Capabilities column for a location row, on both the Edges page and the
 * Locations page. Shows the first few technologies inline and puts everything
 * else behind a click — the overflow used to render as a dead "+3 / +2
 * features" with no way to see what it stood for.
 *
 * Mirrors the Networks column's popover in the same tables, so the two
 * overflow affordances behave identically.
 */

import React from 'react';
import type { AggregatedCapabilities } from '@simcheck/shared-types';
import { Badge, Popover, PopoverTrigger, PopoverContent } from '@/components/ui';

interface CapabilitiesCellProps {
  capabilities?: AggregatedCapabilities;
  /** Technologies shown inline before the rest collapses into the popover. */
  inlineCount?: number;
}

export const CapabilitiesCell: React.FC<CapabilitiesCellProps> = ({
  capabilities,
  inlineCount = 3,
}) => {
  const technologies = capabilities?.technologies ?? [];
  const features = capabilities?.features ?? [];

  if (technologies.length === 0 && features.length === 0) {
    return <span className="text-muted-foreground/70 text-sm">No data</span>;
  }

  const inline = technologies.slice(0, inlineCount);
  const hiddenTech = technologies.length - inline.length;
  const hiddenCount = hiddenTech + features.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {inline.map((tech) => (
        <Badge key={tech} variant="outline" className="text-xs px-1.5 py-0">
          {tech}
        </Badge>
      ))}

      {hiddenCount > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-primary-500 transition-colors underline-offset-2 hover:underline"
              aria-label="Show all capabilities"
            >
              +{hiddenCount} more
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-0">
            <div className="px-3 py-2 border-b border-border">
              <p className="text-sm font-medium text-foreground">Capabilities</p>
              <p className="text-xs text-muted-foreground">
                Across every modem at this location
              </p>
            </div>
            <div className="max-h-64 overflow-y-auto px-3 py-2 space-y-3">
              {technologies.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground/70 mb-1.5">Technologies</p>
                  <div className="flex flex-wrap gap-1">
                    {technologies.map((tech) => (
                      <Badge key={tech} variant="outline" className="text-xs px-1.5 py-0">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {features.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground/70 mb-1.5">Features</p>
                  <div className="flex flex-wrap gap-1">
                    {features.map((feature) => (
                      <Badge key={feature} variant="secondary" className="text-xs px-1.5 py-0">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
